import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, Home, ArrowRight, TrendingUp, Bell } from 'lucide-react';

/**
 * DeprecatedPage - Eski uygulama sayfaları için 410 Gone sayfası
 * 
 * SEO NOTLARI:
 * - noindex, nofollow meta tag eklenir (Google index'ten çıkarsın)
 * - robots.txt'de bu sayfalar ENGELLENMEMELİ (Google noindex'i görmeli)
 * - Kullanıcıyı yeni sayfalara yönlendirir
 */

// Eski URL -> Yeni URL eşleştirmeleri (301 redirect mantığı)
const REDIRECT_MAP = {
  '/marketplace': '/',
  '/subscriber': '/pricing',
  '/trader': '/nasil-calisir',
  '/seller': '/',
  '/partner': '/',
  '/strategies': '/signals',
  '/strategy-builder': '/',
  '/visual-strategy-builder': '/',
  '/guided-strategy-builder': '/',
  '/strategy-wizard': '/',
  '/quick-strategy': '/',
  '/backtest': '/',
  '/analyst': '/',
  '/rehber': '/sss',
  '/bots': '/',
  '/trading-bots': '/',
  '/beta': '/',
};

const DeprecatedPage = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Bu eski URL için önerilen yeni URL
  const suggestedUrl = REDIRECT_MAP[currentPath] || '/';
  
  useEffect(() => {
    // Sayfa başlığını güncelle
    document.title = 'Sayfa Kaldırıldı | Borsamex';
    
    // ═══════════════════════════════════════════════════════════════════
    // KRİTİK SEO: noindex, nofollow meta tag ekle
    // Bu Google'a "bu sayfayı index'ten çıkar" der
    // ═══════════════════════════════════════════════════════════════════
    
    // Mevcut robots meta'yı güncelle veya yeni oluştur
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, follow';
    
    // Googlebot için ayrı meta (bazı durumlarda gerekli)
    let metaGooglebot = document.querySelector('meta[name="googlebot"]');
    if (!metaGooglebot) {
      metaGooglebot = document.createElement('meta');
      metaGooglebot.name = 'googlebot';
      document.head.appendChild(metaGooglebot);
    }
    metaGooglebot.content = 'noindex, follow';
    
    // Canonical URL'i ana sayfaya yönlendir (duplicate content önleme)
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://borsamex.com${suggestedUrl}`;
    
    // Console log (debug için)
    console.log(`[SEO] Deprecated page: ${currentPath} -> noindex, canonical: ${suggestedUrl}`);
    
    return () => {
      // Cleanup: varsayılan değerlere dön
      if (metaRobots) metaRobots.content = 'index, follow';
      if (metaGooglebot) metaGooglebot.content = 'index, follow';
    };
  }, [currentPath, suggestedUrl]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/30">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
          </div>
        </div>
        
        {/* Başlık */}
        <h1 className="text-3xl font-bold text-white mb-4">
          Bu Sayfa Kaldırıldı
        </h1>
        
        {/* Açıklama */}
        <p className="text-slate-400 mb-8 leading-relaxed">
          Aradığınız sayfa (<code className="text-amber-400 bg-slate-800 px-2 py-0.5 rounded">{currentPath}</code>) artık mevcut değil. 
          Borsamex platformu yenilenmiş ve artık <span className="text-brand-green font-medium">AI destekli kripto sinyalleri</span> sunuyor.
        </p>
        
        {/* Yeni özellikler */}
        <div className="bg-slate-800/50 rounded-xl p-6 mb-8 text-left">
          <h2 className="text-lg font-semibold text-white mb-4">Yeni Borsamex ile:</h2>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
              <span>AI destekli <strong>AL/SAT sinyalleri</strong> alın</span>
            </li>
            <li className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
              <span><strong>Telegram</strong> üzerinden anlık bildirimler</span>
            </li>
            <li className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
              <span>3 kademeli <strong>TP hedefleri</strong> ile kar optimizasyonu</span>
            </li>
          </ul>
        </div>
        
        {/* CTA Butonları */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-green text-slate-900 font-semibold rounded-lg hover:bg-brand-green/90 transition-colors"
          >
            <Home className="w-5 h-5" />
            Ana Sayfaya Git
          </Link>
          <Link 
            to="/nasil-calisir"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition-colors"
          >
            Nasıl Çalışır?
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        
        {/* Önerilen sayfa linki */}
        {suggestedUrl !== '/' && (
          <p className="mt-6 text-sm text-slate-500">
            Belki şunu arıyordunuz: {' '}
            <Link to={suggestedUrl} className="text-brand-green hover:underline">
              {suggestedUrl}
            </Link>
          </p>
        )}
        
        {/* SEO için gizli metin - Google bu içeriği okuyacak */}
        <div className="sr-only" aria-hidden="true">
          <p>HTTP 410 Gone - Bu sayfa kalıcı olarak kaldırılmıştır.</p>
          <p>Trading botları, strateji oluşturucu ve pazaryeri özellikleri artık mevcut değildir.</p>
          <p>Borsamex artık AI destekli kripto sinyal platformu olarak hizmet vermektedir.</p>
          <p>Yeni özellikler: AI trading sinyalleri, Telegram bildirimleri, 3 kademeli TP hedefleri.</p>
        </div>
      </div>
    </div>
  );
};

export default DeprecatedPage;
